import { useContext, useMemo, useState } from 'react'
import { Button, MenuItem, Select, Typography } from '@mui/material'
import { enqueueSnackbar } from 'notistack'

import { AppContext } from '/providers/app-context'
import { getManagedPassExport } from '/providers/passes-api'
import PageShell from '/components/statistics/page-shell'
import StyledPaper from '/components/paper'

const StatisticsManagedPassData = () => {
  const { apiSource, apiKey, templates = [] } = useContext(AppContext)
  const [templateId, setTemplateId] = useState('')
  const [processing, setProcessing] = useState(false)
  const selectableTemplates = useMemo(
    () => (templates || [])
      .filter(template => !template.archived && Number(template.managed_pass_template) === 1)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [templates]
  )

  const requestExport = async () => {
    if (!templateId) {
      enqueueSnackbar({ message: 'Choose a managed template first', variant: 'warning' })
      return
    }

    setProcessing(true)
    const result = await getManagedPassExport({ apiSource, apiKey, templateId, mode: 'live' })
    setProcessing(false)

    if (result?.ok && result?.download_url) {
      enqueueSnackbar({ message: 'Managed pass export is ready to download', variant: 'success' })
      window.open(result.download_url, '_blank', 'noopener,noreferrer')
      return
    }

    if (Number(result?.error?.status_code) === 429) return

    enqueueSnackbar({ message: result?.error?.message ?? result?.error ?? 'Error generating managed pass export', variant: 'error' })
  }

  return (
    <PageShell title="Managed Pass Data" heading="Reports & Statistics - Managed Pass Data">
      <div className='flex flex-col gap-5 mt-5 pb-20'>
        <StyledPaper>
          <div className='p-5 flex flex-col gap-4'>
            <Typography variant='h5'>Managed Pass Data</Typography>
            <Typography variant='body2'>Generate a bulk export of managed data for one managed template. The API returns a signed S3 download link.</Typography>
            <div className='flex flex-row flex-wrap items-center gap-4'>
              <Select
                size='small'
                value={templateId}
                onChange={e => setTemplateId(e.target.value)}
                displayEmpty
                renderValue={selected => {
                  if (!selected) return 'Choose managed template'
                  return selectableTemplates.find(template => template.template_id === selected)?.name || selected
                }}
                sx={{ minWidth: 320 }}
              >
                <MenuItem value="" disabled>Choose managed template</MenuItem>
                {selectableTemplates.map(template => (
                  <MenuItem key={template.template_id} value={template.template_id}>{template.name}</MenuItem>
                ))}
              </Select>
              <Button variant='contained' onClick={requestExport} disabled={processing || !templateId}>
                {processing ? 'Generating…' : 'Generate export'}
              </Button>
            </div>
            <Typography variant='caption'>Bulk exports are limited to once per hour per template.</Typography>
          </div>
        </StyledPaper>
      </div>
    </PageShell>
  )
}

export default StatisticsManagedPassData
